import type { GetCurrentUserFn, GetCurrentWorkspaceFn } from '@/features/iam'
import type { UserStore } from '@/features/iam/users/UserStore'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { pattern, required, validationContext } from 'validx'

/**
 * View model for the profile editor.
 */
export class ResetPasswordViewModel {
  readonly modal = new ModalViewModel()

  readonly validation = validationContext(this, {
    email: [
      required('Email address is required.'),
      pattern('email', 'Email address is invalid.'),
    ],
  })

  @observable
  accessor email = ''

  @observable
  accessor submitted = false

  constructor(
    private readonly _store: UserStore,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {
    this.modal.show$.subscribe(this._reset)
  }

  @computed
  get workspace() {
    return this._getCurrentWorkspace()
  }

  @computed
  get user() {
    return this._getCurrentUser()
  }

  @action.bound
  open() {
    this.modal.show()
  }

  @action.bound
  close() {
    this.modal.close()
  }

  @action.bound
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    await this._store
      .requestPasswordReset(this.email, this.workspace.id)
      .catch(
        this._createSnackbarErrorHandler(
          'Failed to request the reset password',
        ),
      )

    this.submitted = true
  })

  @action.bound
  private _reset() {
    this.submitted = false
    this.email = this.user.email ?? ''
  }
}
